import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Descriptions, Form, Modal, Tag, notification } from 'antd';
import { useRouteMatch } from 'react-router';

import { queryClient } from '@/Root';

import { QuotationItem } from '@/core/domain/stock/Quotations/Quotations.types';
import { useUpdateQuotationItem } from '@/core/domain/stock/Quotations/Quotations.hooks';

import QuotationItemForm, { QuotationItemFormData } from '../QuotationItemForm';
import { StockItemTypeOptions } from '@/core/domain/stock/StockItems/StockItems.types';

interface UpdateQuotationItemModalProps {
  visible: boolean;
  handleVisible: (quotationItem?: QuotationItem) => void;
  quotationItem?: QuotationItem;
}

const UpdateQuotationItemModal = ({
  visible,
  handleVisible,
  quotationItem,
}: UpdateQuotationItemModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<QuotationItemFormData>();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;

  useEffect(() => {
    if (visible && quotationItem) {
      form.setFieldsValue({
        quantity: quotationItem.quantity,
        manufacturerId: quotationItem.manufacturer.id,
      });
    }
  }, [visible, quotationItem]);

  const mutation = useUpdateQuotationItem(+quotationId);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (!quotationItem) return;

      const obj = {
        ...values,
        id: +quotationItem?.id,
        quotationId: +quotationId,
      };

      await mutation.mutateAsync(obj);

      await queryClient.refetchQueries(['quotations', +quotationId, 'items']);

      notification.success({
        message: intl.formatMessage({
          id: 'quotations.details.items.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'quotations.details.items.update.notification.successDescription',
        }),
      });

      handleVisible();
    } catch {}
  };

  const handleCancel = () => {
    handleVisible();
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'quotations.details.items.update' })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={mutation.isLoading}
    >
      <Descriptions column={2}>
        <Descriptions.Item
          label={intl.formatMessage({
            id: 'quotations.details.items.manufacturingCode',
          })}
        >
          {quotationItem?.manufacturingCode}
        </Descriptions.Item>
        <Descriptions.Item
          label={intl.formatMessage({ id: 'quotations.details.items.type' })}
        >
          <Tag color="processing">
            {quotationItem ? (
              <FormattedMessage
                id={`quotations.domain.enums.item_type.${StockItemTypeOptions[
                  quotationItem?.type
                ]
                  .split('.')
                  .pop()}`}
              />
            ) : null}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          span={2}
          label={intl.formatMessage({ id: 'quotations.details.items.name' })}
        >
          {quotationItem?.name}
        </Descriptions.Item>
      </Descriptions>

      <QuotationItemForm form={form} quotationItem={quotationItem} />
    </Modal>
  );
};

export default UpdateQuotationItemModal;
